import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  CircularProgress,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import MenuIcon from '@mui/icons-material/Menu';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  listContacts,
  deleteContact,
  getContactDisplayName,
  getContactInitials,
  updateContactFlags,
  getContactFolders,
  createContactFolder,
  updateContactFolder,
  deleteContactFolder,
  moveContactToFolder,
} from '../services/contacts';
import { ContactFlags, hasFlag } from '../types';
import type { Contact, ContactGroup, ContactFolder } from '../types';
import {
  listContactGroups,
  deleteContactGroup,
} from '../services/contact-groups';
import ContactDialog from '../components/contacts/ContactDialog';
import ContactGroupDialog from '../components/contacts/ContactGroupDialog';
import ContactGroupMembersDialog from '../components/contacts/ContactGroupMembersDialog';
import ContactImportDialog from '../components/contacts/ContactImportDialog';
import RenameDialog from '../components/documents/RenameDialog';

type ContactView = 'starred' | 'all' | string;

export default function ContactsPage() {
  const [tab, setTab] = useState(0);

  // --- Folder sidebar state ---
  const [contactView, setContactView] = useState<ContactView>('all');
  const [folders, setFolders] = useState<ContactFolder[]>([]);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [folderMenuTarget, setFolderMenuTarget] = useState<ContactFolder | null>(null);
  const [renameFolder, setRenameFolder] = useState<ContactFolder | null>(null);
  const [contactMenuAnchor, setContactMenuAnchor] = useState<null | HTMLElement>(null);
  const [contactMenuTarget, setContactMenuTarget] = useState<Contact | null>(null);
  const [foldersExpanded, setFoldersExpanded] = useState(() => localStorage.getItem('contacts_foldersExpanded') !== 'false');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('contacts_sidebarOpen') !== 'false');

  // --- Contacts state ---
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // --- Groups state ---
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<ContactGroup | null>(null);
  const [membersGroup, setMembersGroup] = useState<ContactGroup | null>(null);

  // --- Folder logic ---
  const loadFolders = useCallback(async () => {
    getContactFolders().then(setFolders).catch(() => {});
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  const handleDeleteFolder = async (folder: ContactFolder) => {
    if (!confirm(`Delete folder "${folder.name}"? Contacts will become ungrouped.`)) return;
    await deleteContactFolder(folder.id);
    loadFolders();
    if (contactView === folder.id) setContactView('all');
  };

  // --- Contacts logic ---
  const loadContacts = useCallback(async (filter?: string) => {
    setLoadingContacts(true);
    try {
      const opts =
        contactView === 'starred' ? { starred: true } :
        contactView === 'all' ? undefined :
        { folderId: contactView };
      const result = await listContacts(filter || undefined, opts);
      result.sort((a, b) => {
        if (a.nicknames.includes('me')) return -1;
        if (b.nicknames.includes('me')) return 1;
        return 0;
      });
      setContacts(result);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  }, [contactView]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadContacts(search);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, loadContacts]);

  const handleDeleteContact = async (e: React.MouseEvent, contact: Contact) => {
    e.stopPropagation();
    const name = getContactDisplayName(contact);
    if (!window.confirm(`Delete contact "${name}"?`)) return;
    try {
      await deleteContact(contact.id);
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const handleSaved = () => { loadContacts(search); };

  // --- Groups logic ---
  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const result = await listContactGroups();
      setGroups(result);
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const handleDeleteGroup = async (e: React.MouseEvent, group: ContactGroup) => {
    e.stopPropagation();
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    try {
      await deleteContactGroup(group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
    } catch (err) {
      console.error('Failed to delete group:', err);
    }
  };

  const viewTitle =
    contactView === 'starred' ? 'Starred' :
    contactView === 'all' ? 'All Contacts' :
    folders.find(f => f.id === contactView)?.name ?? 'Contacts';

  const systemViews = [
    { id: 'all', label: 'All Contacts' },
    { id: 'starred', label: 'Starred', starred: true },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Contacts" />
        <Tab label="Groups" />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left panel */}
          <Box sx={{ width: sidebarOpen ? 220 : 48, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'auto', transition: 'width 0.2s' }}>
            <Box sx={{ p: sidebarOpen ? 1.5 : 0.5, display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
              <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
                <IconButton size="small" onClick={() => setSidebarOpen(prev => { const next = !prev; localStorage.setItem('contacts_sidebarOpen', String(next)); return next; })}>
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {sidebarOpen && <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, ml: 1 }}>Folders</Typography>}
              {sidebarOpen && (
                <Tooltip title="New folder">
                  <IconButton size="small" onClick={async () => {
                    const name = prompt('Folder name:');
                    if (!name?.trim()) return;
                    const f = await createContactFolder(name.trim());
                    setFolders(prev => [...prev, f].sort((a, b) => a.name.localeCompare(b.name)));
                  }}>
                    <CreateNewFolderIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <List dense disablePadding>
              {systemViews.map((v) => (
                <ListItem key={v.id} disablePadding>
                  <Tooltip title={sidebarOpen ? '' : v.label} placement="right">
                    <ListItemButton selected={contactView === v.id} onClick={() => setContactView(v.id as ContactView)} sx={{ borderRadius: 1, mx: 0.5, justifyContent: sidebarOpen ? 'initial' : 'center' }}>
                      <ListItemIcon sx={{ minWidth: sidebarOpen ? 32 : 'auto' }}>
                        {v.starred
                          ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          : contactView === v.id ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                      </ListItemIcon>
                      {sidebarOpen && <ListItemText primary={v.label} primaryTypographyProps={{ variant: 'body2' }} />}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
            {sidebarOpen && folders.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <ListItemButton
                  onClick={() => setFoldersExpanded(prev => { const next = !prev; localStorage.setItem('contacts_foldersExpanded', String(next)); return next; })}
                  sx={{ py: 0.5, px: 1.5 }}
                >
                  <ListItemText primary="My Folders" primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }} />
                  {foldersExpanded ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                </ListItemButton>
                <Collapse in={foldersExpanded} timeout="auto" unmountOnExit>
                  <List dense disablePadding>
                    {folders.map((f) => (
                      <ListItem key={f.id} disablePadding secondaryAction={
                        <IconButton size="small" edge="end" onClick={(e) => { setFolderMenuAnchor(e.currentTarget); setFolderMenuTarget(f); }}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      }>
                        <ListItemButton selected={contactView === f.id} onClick={() => setContactView(f.id)} sx={{ borderRadius: 1, mx: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {contactView === f.id ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText primary={f.name} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </Box>

          {/* Right panel */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">{viewTitle}</Typography>
              <Stack direction="row" gap={1}>
                <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>Import</Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditContact(null); setDialogOpen(true); }}>Add Contact</Button>
              </Stack>
            </Stack>

            <TextField
              placeholder="Search contacts..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {loadingContacts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
            ) : contacts.length === 0 ? (
              <Typography color="text.secondary">
                {search ? 'No contacts match your search.' : 'No contacts here.'}
              </Typography>
            ) : (
              <List disablePadding>
                {contacts.map((c) => {
                  const displayName = getContactDisplayName(c);
                  const initials = getContactInitials(c);
                  const secondary = [c.email, c.phone].filter(Boolean).join(' \u00B7 ');

                  return (
                    <ListItemButton
                      key={c.id}
                      onClick={() => { setEditContact(c); setDialogOpen(true); }}
                      sx={{ borderRadius: 2, mb: 0.5 }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newFlags = hasFlag(c.flags, ContactFlags.STARRED)
                            ? c.flags & ~ContactFlags.STARRED
                            : c.flags | ContactFlags.STARRED;
                          setContacts((prev) => prev.map((ct) => ct.id === c.id ? { ...ct, flags: newFlags } : ct));
                          updateContactFlags(c.id, newFlags);
                        }}
                        sx={{ p: 0.25, mr: 0.5, color: hasFlag(c.flags, ContactFlags.STARRED) ? 'warning.main' : 'action.disabled' }}
                      >
                        {hasFlag(c.flags, ContactFlags.STARRED) ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                      <ListItemAvatar>
                        <Avatar src={c.photoUrl || undefined}>{initials}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={displayName} secondary={secondary || undefined} />
                      {!c.nicknames.includes('me') && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setContactMenuAnchor(e.currentTarget); setContactMenuTarget(c); }} sx={{ ml: 1 }}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto', width: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Groups</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditGroup(null); setGroupDialogOpen(true); }}>
              Create Group
            </Button>
          </Stack>

          {loadingGroups ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
          ) : groups.length === 0 ? (
            <Typography color="text.secondary">No groups yet.</Typography>
          ) : (
            <List disablePadding>
              {groups.map((g) => (
                <ListItemButton key={g.id} onClick={() => setMembersGroup(g)} sx={{ borderRadius: 2, mb: 0.5 }}>
                  <ListItemAvatar>
                    <Avatar><GroupIcon /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={g.name}
                    secondary={[g.description, `${g.memberCount} member${g.memberCount !== 1 ? 's' : ''}`].filter(Boolean).join(' \u00B7 ')}
                  />
                  <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); setEditGroup(g); setGroupDialogOpen(true); }} sx={{ ml: 1 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton edge="end" size="small" onClick={(e) => handleDeleteGroup(e, g)} sx={{ ml: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* Folder context menu */}
      <Menu anchorEl={folderMenuAnchor} open={Boolean(folderMenuAnchor)} onClose={() => setFolderMenuAnchor(null)}>
        <MenuItem onClick={() => { setRenameFolder(folderMenuTarget); setFolderMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
        </MenuItem>
        <MenuItem onClick={() => { if (folderMenuTarget) handleDeleteFolder(folderMenuTarget); setFolderMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
        </MenuItem>
      </Menu>

      {/* Contact context menu */}
      <Menu anchorEl={contactMenuAnchor} open={Boolean(contactMenuAnchor)} onClose={() => setContactMenuAnchor(null)}>
        <MenuItem onClick={() => { if (contactMenuTarget) { setEditContact(contactMenuTarget); setDialogOpen(true); } setContactMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Edit
        </MenuItem>
        {folders.length > 0 && (
          <MenuItem onClick={() => {
            if (!contactMenuTarget) { setContactMenuAnchor(null); return; }
            // Simple: show submenu by using prompt for now
            const opts = folders.map(f => `${f.name}`).join('\n');
            const name = prompt(`Move to folder:\n${opts}\n(type folder name or leave blank to remove)`);
            const folder = name ? folders.find(f => f.name.toLowerCase() === name.toLowerCase()) : null;
            moveContactToFolder(contactMenuTarget.id, folder?.id ?? null).then(() => loadContacts(search));
            setContactMenuAnchor(null);
          }}>
            <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>Move to folder
          </MenuItem>
        )}
        <MenuItem onClick={(e) => { if (contactMenuTarget) handleDeleteContact(e, contactMenuTarget); setContactMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <ContactDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        contact={editContact}
        onSaved={handleSaved}
      />
      <ContactImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => loadContacts(search)}
      />
      <ContactGroupDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        group={editGroup}
        onSaved={loadGroups}
      />
      {membersGroup && (
        <ContactGroupMembersDialog
          open={!!membersGroup}
          onClose={() => setMembersGroup(null)}
          group={membersGroup}
          onUpdated={loadGroups}
        />
      )}
      {renameFolder && (
        <RenameDialog
          open
          title="Rename folder"
          currentName={renameFolder.name}
          onClose={() => setRenameFolder(null)}
          onSave={async (name) => {
            await updateContactFolder(renameFolder.id, name);
            setRenameFolder(null);
            loadFolders();
          }}
        />
      )}
    </Box>
  );
}
